import React, { useEffect, useState } from 'react';
import '../App.css';  // Import CSS styles
import { Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  buildPhoneNumber,
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  isEmailAddress,
} from '../utils/phoneCountries';

const loginCopy = {
  hindi: {
    languageToggleLabel: 'भाषा',
    title: 'वापस स्वागत है',
    subtitle: 'अपनी सीखने की यात्रा जारी रखने के लिए साइन इन करें',
    emailPhoneLabel: 'ईमेल या फोन नंबर',
    countryLabel: 'देश कोड',
    emailPhonePlaceholder: 'अपना ईमेल या फोन नंबर दर्ज करें',
    passwordLabel: 'पासवर्ड',
    passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
    forgotPassword: 'पासवर्ड भूल गए?',
    forgotHeading: 'सत्यापन तरीका चुनें',
    verifyByBirthDate: 'जन्म तिथि से सत्यापित करें',
    verifyByEmailOtp: 'ईमेल OTP से सत्यापित करें',
    sendOtp: 'OTP भेजें',
    sendingOtp: 'OTP भेजा जा रहा है...',
    birthDateLabel: 'जन्म तिथि',
    confirmBirthDate: 'जन्म तिथि की पुष्टि करें',
    confirmingBirthDate: 'पुष्टि की जा रही है...',
    signIn: 'साइन इन करें',
    signingIn: 'साइन इन किया जा रहा है...',
    divider: 'या',
    noAccount: 'क्या आपका अकाउंट नहीं है?',
    signUp: 'साइन अप करें',
    pleaseEnterEmailOrPhone: 'कृपया अपना ईमेल या फोन नंबर दर्ज करें',
    pleaseEnterEmail: 'कृपया अपना ईमेल दर्ज करें',
    pleaseEnterValidEmail: 'कृपया एक मान्य ईमेल पता दर्ज करें',
    pleaseEnterValidEmailOrPhone: 'कृपया एक मान्य ईमेल या फोन नंबर दर्ज करें',
    pleaseSelectBirthDate: 'कृपया जन्म तिथि चुनें',
    noAccountFound: 'दिए गए ईमेल/फोन के लिए कोई खाता नहीं मिला',
    birthDateMismatch: 'जन्म तिथि हमारे रिकॉर्ड से मेल नहीं खाती',
    birthDateVerificationFailed: 'जन्म तिथि सत्यापन विफल हुआ। कृपया पुनः प्रयास करें।',
    invalidCredentials: 'अमान्य उपयोगकर्ता/ईमेल या पासवर्ड',
    networkError: 'नेटवर्क त्रुटि. कृपया बाद में पुनः प्रयास करें.',
    loginFailed: 'लॉगिन विफल हुआ',
    otpSendFailed: 'OTP भेजते समय त्रुटि हुई. कृपया पुनः प्रयास करें.',
    otpSendGenericError: 'त्रुटि हुई. कृपया पुनः प्रयास करें.',
    passwordLength: 'कृपया 4 अक्षर दर्ज करें',
    showPassword: 'पासवर्ड दिखाएं',
    hidePassword: 'पासवर्ड छुपाएं',
  },
  english: {
    languageToggleLabel: 'Language',
    title: 'Welcome Back',
    subtitle: 'Sign in to continue your learning journey',
    emailPhoneLabel: 'Email or Phone Number',
    countryLabel: 'Country Code',
    emailPhonePlaceholder: 'Enter your email or phone number',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    forgotPassword: 'Forgot Password?',
    forgotHeading: 'Choose verification method',
    verifyByBirthDate: 'Verify by Birth Date',
    verifyByEmailOtp: 'Verify by Email OTP',
    sendOtp: 'Send OTP',
    sendingOtp: 'Sending OTP...',
    birthDateLabel: 'Birth Date',
    confirmBirthDate: 'Confirm Birth Date',
    confirmingBirthDate: 'Confirming...',
    signIn: 'Sign In',
    signingIn: 'Signing In...',
    divider: 'OR',
    noAccount: "Don't have an account?",
    signUp: 'Sign Up',
    pleaseEnterEmailOrPhone: 'Please enter your email or phone number',
    pleaseEnterEmail: 'Please enter your email',
    pleaseEnterValidEmail: 'Please enter a valid email address',
    pleaseEnterValidEmailOrPhone: 'Please enter a valid email or phone number',
    pleaseSelectBirthDate: 'Please select birth date',
    noAccountFound: 'No account found for entered email/phone',
    birthDateMismatch: 'Birth date does not match our records',
    birthDateVerificationFailed: 'Birth date verification failed. Please try again.',
    invalidCredentials: 'Invalid username/email or password',
    networkError: 'Network error. Please try again later.',
    loginFailed: 'Login failed',
    otpSendFailed: 'An error occurred while sending OTP. Please try again.',
    otpSendGenericError: 'An error occurred. Please try again.',
    passwordLength: 'Please enter 4 characters',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
  },
};

const isPhoneLikeInput = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes("@")) {
    return false;
  }

  return /^[+\d()\-\s]+$/.test(trimmed) && /\d/.test(trimmed);
};

function Login() {
  const navigate = useNavigate();
  const [userinput, setUserInput] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  const [language, setLanguage] = useState(() => localStorage.getItem('loginLanguage') || 'hindi');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [errorMessage, setErrorMessage] = useState(''); 
  const [loading, setLoading] = useState(false);
  const [forgotMethod, setForgotMethod] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  let authToken = `${process.env.REACT_APP_BASE_TOKEN}`;
  const text = loginCopy[language] || loginCopy.hindi;

  useEffect(() => {
    localStorage.setItem('loginLanguage', language);
  }, [language]);

  const toggleShowPassword = () => {
    setShowPassword((prevState) => !prevState);
  };

  const handlePasswordChange = (text) => {
    setPassword(text);
    if (text.length < 4) {
      setPasswordError(loginCopy[language]?.passwordLength || loginCopy.hindi.passwordLength);
    } else {
      setPasswordError("");
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setErrorMessage(''); // Reset any previous error message
    const trimmedInput = userinput.trim();
    const isEmailInput = isEmailAddress(trimmedInput);
    const isPhoneInput = isPhoneLikeInput(trimmedInput);
    const loginIdentifier = isEmailInput
      ? trimmedInput.toLowerCase()
      : isPhoneInput
        ? buildPhoneNumber(trimmedInput, phoneCountryCode)
        : trimmedInput;

    if (!loginIdentifier) {
      setErrorMessage(text.pleaseEnterEmailOrPhone);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(
        'https://api.japaking.online/api/users/login', 
        {
          userNameOrEmail: loginIdentifier,
          password: password, // Use the password state here
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        // Handle successful login here
        localStorage.setItem('AuthToken', response?.data?.message?.token); // Save token to localStorage
        console.log('Login successful:', response.data);
        console.log('Auth Token stored successfully:', response?.data?.message?.token);

        const japakingtoken = response?.data?.message?.token;
            document.cookie = `AuthToken=${japakingtoken}; path=/; domain = https://api.japaking.online; secure; SameSite=none`;

            console.log('Login successful:', response.data);
            console.log('Auth japakingtoken stored in cookie:', japakingtoken);
            
            window.location.href = '/';
      } else {
        setErrorMessage(response.data.message || text.loginFailed);
      }
    } catch (error) {
      console.log("Full error response:", error.response); // Log the full error response to inspect it
      if (error.response) {
        // Handle the error based on status code
        if (error.response.status === 400) {
          setErrorMessage(text.invalidCredentials); // Custom error message for status 400
        } else {
          const message = error.response?.data?.message || error.response?.data?.errors || text.otpSendGenericError;
          setErrorMessage(typeof message === 'string' ? message : JSON.stringify(message));
        }
      } else {
        // If no response object exists, it's likely a network error
        setErrorMessage(text.networkError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotByEmail = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!userinput) {
      setErrorMessage(text.pleaseEnterEmail);
      return;
    }
  
    if (!emailPattern.test(userinput)) {
      setErrorMessage(text.pleaseEnterValidEmail);
      return;
    }

    setForgotLoading(true);
    setErrorMessage('');
  
    try {
      const response = await axios.post('https://api.japaking.online/api/otp/send', {
        email: userinput
      });
      
      if (response.data.success) {
        navigate("/user-otp", { state: { email: userinput } });
      } else {
        setErrorMessage(text.otpSendGenericError);
      }
    } catch (error) {
      setErrorMessage(text.otpSendFailed);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotByBirthdate = async () => {
    const isEmailInput = isEmailAddress(userinput);
    const normalizedPhone = buildPhoneNumber(userinput, phoneCountryCode);

    if (!userinput.trim()) {
      setErrorMessage(text.pleaseEnterEmailOrPhone);
      return;
    }

    if (!isEmailInput && !normalizedPhone) {
      setErrorMessage(text.pleaseEnterValidEmailOrPhone);
      return;
    }

    if (!birthdate) {
      setErrorMessage(text.pleaseSelectBirthDate);
      return;
    }

    setForgotLoading(true);
    setErrorMessage('');

    try {
      const queryParam = isEmailInput
        ? `email=${encodeURIComponent(userinput.trim().toLowerCase())}`
        : `phone_number=${encodeURIComponent(normalizedPhone)}`;

      const response = await axios.get(`https://api.japaking.online/api/users/verify-birthdate?${queryParam}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const users = response?.data?.message?.result?.users || [];
      const matchedDobUsers = users.filter((user) => user?.dob === birthdate);

      if (!users.length) {
        setErrorMessage(text.noAccountFound);
        return;
      }

      if (!matchedDobUsers.length) {
        setErrorMessage(text.birthDateMismatch);
        return;
      }

      const user = matchedDobUsers[0];
      navigate("/password-change", {
        state: {
          resetMethod: 'birthdate',
          userId: user?.user_id,
          name: user?.name,
          email: user?.email,
          phone_number: user?.phone_number,
          birthdate,
        },
      });
    } catch (error) {
      setErrorMessage(text.birthDateVerificationFailed);
    } finally {
      setForgotLoading(false);
    }
  };

  const showPhoneLayout = isPhoneLikeInput(userinput);

  return (
    <div className="login-v2-page">
      <div className="login-v2-card">
        <div className="login-v2-language-toggle" role="tablist" aria-label={text.languageToggleLabel}>
          <button
            type="button"
            className={`login-v2-language-option ${language === 'hindi' ? 'active' : ''}`}
            onClick={() => setLanguage('hindi')}
          >
            हिंदी
          </button>
          <button
            type="button"
            className={`login-v2-language-option ${language === 'english' ? 'active' : ''}`}
            onClick={() => setLanguage('english')}
          >
            English
          </button>
        </div>

        <h1 className="login-v2-title">{text.title}</h1>
        <p className="login-v2-subtitle">{text.subtitle}</p>

        <div className="login-v2-field-block">
          <label className="login-v2-label">{text.emailPhoneLabel}</label>
          {showPhoneLayout ? (
            <div className="phone-country-row login-phone-country-row">
              <div className="phone-country-select-wrap login-phone-country-select-wrap">
                <label className="phone-country-label" htmlFor="login-phone-country-select">
                  {text.countryLabel}
                </label>
                <select
                  id="login-phone-country-select"
                  className="phone-country-select"
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                >
                  {PHONE_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="phone-country-input-wrap login-phone-country-input-wrap">
                <input
                  id="login-phone-input"
                  className="login-v2-input phone-country-input"
                  placeholder={text.emailPhonePlaceholder}
                  aria-label={text.emailPhoneLabel}
                  onChange={(e) => setUserInput(e.target.value)}
                  value={userinput}
                />
              </div>
            </div>
          ) : (
            <input
              id="login-phone-input"
              className="login-v2-input"
              placeholder={text.emailPhonePlaceholder}
              aria-label={text.emailPhoneLabel}
              onChange={(e) => setUserInput(e.target.value)}
              value={userinput}
            />
          )}
        </div>

        <div className="login-v2-field-block">
          <label className="login-v2-label">{text.passwordLabel}</label>
          <div className="login-v2-password-wrap">
            <input
              className="login-v2-input login-v2-password-input"
              placeholder={text.passwordPlaceholder}
              type={showPassword ? 'text' : 'password'}
              onChange={(e) => handlePasswordChange(e.target.value)}
              value={password}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={toggleShowPassword}
              className="login-v2-visibility"
              aria-label={showPassword ? text.hidePassword : text.showPassword}
            >
              {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </button>
          </div>
        </div>

        <div className="login-v2-forgot-wrap">
          {passwordError && <p className="login-v2-password-error">{passwordError}</p>}
          <p
            className="login-v2-forgot"
            onClick={() => {
              setErrorMessage('');
              setForgotMethod((prev) => (prev ? '' : 'birthdate'));
              setBirthdate('');
            }}
          >
            {text.forgotPassword}
          </p>
        </div>

        {forgotMethod && (
          <div className="login-v2-forgot-panel">
            <p className="login-v2-forgot-heading">{text.forgotHeading}</p>

            <div className="login-v2-forgot-methods">
              <button
                type="button"
                className={`login-v2-method-btn ${forgotMethod === 'birthdate' ? 'active' : ''}`}
                onClick={() => {
                  setForgotMethod('birthdate');
                  setErrorMessage('');
                }}
              >
                {text.verifyByBirthDate}
              </button>
              <button
                type="button"
                className={`login-v2-method-btn ${forgotMethod === 'email' ? 'active' : ''}`}
                onClick={() => {
                  setForgotMethod('email');
                  setErrorMessage('');
                }}
              >
                {text.verifyByEmailOtp}
              </button>
            </div>

            {forgotMethod === 'email' && (
              <button
                className="login-v2-button login-v2-secondary-button"
                onClick={handleForgotByEmail}
                disabled={forgotLoading}
              >
                {forgotLoading ? text.sendingOtp : text.sendOtp}
              </button>
            )}

            {forgotMethod === 'birthdate' && (
              <>
                <div className="login-v2-field-block login-v2-forgot-field">
                  <label className="login-v2-label">{text.birthDateLabel}</label>
                  <input
                    type="date"
                    className="login-v2-input"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                  />
                </div>

                <button
                  className="login-v2-button login-v2-confirm-button"
                  onClick={handleForgotByBirthdate}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? text.confirmingBirthDate : text.confirmBirthDate}
                </button>
              </>
            )}
          </div>
        )}

        {errorMessage && (
          <Alert variant="danger" className="mt-2">
            {errorMessage}
          </Alert>
        )}

        <button
          className="login-v2-button"
          onClick={handleLogin}
          disabled={!userinput || password.length < 4}
        >
          {loading ? text.signingIn : text.signIn}
        </button>

        <div className="login-v2-divider">
          <span>{text.divider}</span>
        </div>

        <p className="login-v2-signup-text">
          {text.noAccount}{" "}
          <Link to="/signup" className="login-v2-signup-link">
            {text.signUp}
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
